// Persistent lobby layout: top bar + bottom nav that stay visible on every
// non-match menu screen. Children render inside a container that never
// unmounts the 3D lobby background, so transitions feel continuous.
import { useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { useUiSounds } from "@/lib/audio";
import { useT } from "@/i18n";
import LobbyBackground from "@/components/LobbyBackground";
import ExitDialog from "@/components/ExitDialog";
import { BRAND } from "@/config/game";
import { MENU } from "@/constants/testIds";
import {
    Home,
    Settings as SettingsIcon,
    LogOut,
    Package,
    Layers,
    Play,
    ShoppingBag,
    Newspaper,
    Users,
    Video,
    User,
} from "lucide-react";

// Central nav
const CENTER_ITEMS = [
    { id: SCREEN.INVENTORY, icon: Package, key: "nav.inventory", testId: MENU.inventory },
    { id: SCREEN.LOADOUT, icon: Layers, key: "nav.loadout", testId: "menu-loadout" },
    { id: SCREEN.PLAY, icon: Play, key: "nav.play", testId: MENU.play, primary: true },
    { id: SCREEN.STORE, icon: ShoppingBag, key: "nav.store", testId: MENU.store },
    { id: SCREEN.NEWS, icon: Newspaper, key: "nav.news", testId: MENU.news },
];

// Left nav
const LEFT_ITEMS = [
    { id: SCREEN.MENU, icon: Home, key: "nav.home", testId: "menu-home" },
    { id: SCREEN.SETTINGS, icon: SettingsIcon, key: "nav.settings", testId: MENU.settings },
    { id: "__exit__", icon: LogOut, key: "nav.exit", testId: MENU.exit, danger: true },
];

// Right nav
const RIGHT_ITEMS = [
    { id: SCREEN.FRIENDS, icon: Users, key: "nav.friends", testId: MENU.friends },
    { id: SCREEN.RECORDINGS, icon: Video, key: "nav.recordings", testId: "menu-recordings" },
    { id: SCREEN.PROFILE, icon: User, key: "nav.profile", testId: MENU.profile },
];

export default function LobbyLayout({ children, active, hideBackground = false }) {
    const goto = useGameStore((s) => s.goto);
    const sfx = useUiSounds();
    const t = useT();
    const [exitOpen, setExitOpen] = useState(false);

    const handle = (id) => {
        if (id === "__exit__") {
            sfx.open();
            setExitOpen(true);
            return;
        }
        sfx.click();
        goto(id);
    };

    return (
        <div className="relative w-screen h-screen overflow-hidden axis-scanline bg-black">
            {!hideBackground && <LobbyBackground />}

            {/* Top-bar (brand + version + beta) */}
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-8 py-4">
                <div className="flex items-center gap-3">
                    <svg width="32" height="32" viewBox="0 0 34 34">
                        <polygon points="17,3 31,27 3,27" fill="none" stroke="#ffffff" strokeWidth="1.5" />
                        <circle cx="17" cy="18" r="2.2" fill="#ff9a4a" />
                    </svg>
                    <div className="flex flex-col leading-none">
                        <span className="axis-heading text-lg tracking-[0.5em] text-white">
                            {BRAND.game.title}
                        </span>
                        <span className="axis-mono text-[10px] tracking-[0.35em] text-white/45 mt-1">
                            BY {BRAND.company.name} · v{BRAND.version}
                        </span>
                    </div>
                </div>
                <div
                    data-testid={MENU.beta}
                    className="axis-mono text-[10px] tracking-[0.4em] px-3 py-1.5 border border-white/25 text-white/80 axis-cut-sm"
                >
                    {t("common.beta")} · 0.4.0
                </div>
            </div>

            {/* Page content */}
            <div className="absolute inset-0 z-10 pt-16 pb-32 axis-fade-in" style={{ pointerEvents: "none" }}>
                <div className="w-full h-full" style={{ pointerEvents: "auto" }}>
                    {children}
                </div>
            </div>

            {/* Bottom nav */}
            <div className="absolute bottom-0 left-0 right-0 z-30 px-6 pb-5 pt-8 pointer-events-none">
                <div
                    className="max-w-[1600px] mx-auto grid items-center gap-4"
                    style={{ gridTemplateColumns: "1fr auto 1fr", pointerEvents: "none" }}
                >
                    {/* LEFT */}
                    <div className="flex items-center gap-2 justify-start pointer-events-auto">
                        {LEFT_ITEMS.map((item) => (
                            <NavBtn
                                key={item.id}
                                item={item}
                                label={t(item.key)}
                                active={active === item.id}
                                onClick={() => handle(item.id)}
                                onHover={sfx.hover}
                            />
                        ))}
                    </div>

                    {/* CENTER — five items with PLAY largest */}
                    <div className="pointer-events-auto axis-glass axis-cut px-5 py-2 flex items-center gap-2">
                        {CENTER_ITEMS.map((item) => (
                            <CenterBtn
                                key={item.id}
                                item={item}
                                label={t(item.key)}
                                active={active === item.id}
                                onClick={() => handle(item.id)}
                                onHover={sfx.hover}
                            />
                        ))}
                    </div>

                    {/* RIGHT */}
                    <div className="flex items-center gap-2 justify-end pointer-events-auto">
                        {RIGHT_ITEMS.map((item) => (
                            <NavBtn
                                key={item.id}
                                item={item}
                                label={t(item.key)}
                                active={active === item.id}
                                onClick={() => handle(item.id)}
                                onHover={sfx.hover}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <ExitDialog open={exitOpen} onClose={() => setExitOpen(false)} />
        </div>
    );
}

function NavBtn({ item, label, active, onClick, onHover }) {
    const Icon = item.icon;
    return (
        <button
            data-testid={item.testId}
            onClick={onClick}
            onMouseEnter={onHover}
            className={`axis-cut-sm px-3 py-2.5 flex items-center gap-2 text-xs axis-btn transition min-w-[104px] justify-center border ${
                active
                    ? "border-white/70 bg-white/10 text-white"
                    : "border-white/15 bg-black/55 text-white/70 hover:border-white/40 hover:text-white"
            } ${item.danger ? "hover:!border-red-500/70 hover:!text-red-400" : ""}`}
        >
            <Icon size={15} />
            <span className="axis-heading tracking-[0.2em] text-[11px]">{label}</span>
        </button>
    );
}

function CenterBtn({ item, label, active, onClick, onHover }) {
    const Icon = item.icon;
    if (item.primary) {
        return (
            <button
                data-testid={item.testId}
                onClick={onClick}
                onMouseEnter={onHover}
                className={`axis-cut-sm px-9 py-3 flex items-center gap-3 axis-btn transition border ${
                    active
                        ? "bg-orange-400 border-orange-300 text-black"
                        : "bg-orange-500/90 border-orange-400 text-black hover:bg-orange-400"
                }`}
                style={{
                    boxShadow: active
                        ? "0 0 24px rgba(255,150,60,0.6), inset 0 0 24px rgba(255,180,90,0.35)"
                        : "0 0 18px rgba(255,140,60,0.35)",
                }}
            >
                <Icon size={18} className="fill-current" />
                <span className="axis-heading tracking-[0.3em] text-base">{label}</span>
            </button>
        );
    }
    return (
        <button
            data-testid={item.testId}
            onClick={onClick}
            onMouseEnter={onHover}
            className={`axis-cut-sm px-4 py-2.5 flex items-center gap-2 axis-btn transition border ${
                active
                    ? "border-white bg-white/15 text-white"
                    : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
            }`}
        >
            <Icon size={14} />
            <span className="axis-heading tracking-[0.24em] text-[11px]">{label}</span>
        </button>
    );
}
